/**
 * Carbon Design System SVG icons
 * Source: https://github.com/carbon-design-system/carbon
 * License: Apache 2.0 (IBM)
 *
 * All icons are on a 32×32 viewBox, rendered at 24px by default.
 * Use `makeIcon(rawPath, opts)` when a custom size or accessible label is needed.
 *
 * Usage in template literals:
 *   `<button>${Icons.theme.sun}</button>`
 *
 * Custom size or label:
 *   makeIcon(IconPaths.sun, { size: 20, label: 'Light mode' })
 */

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export type IconOptions = {
  size?: number;
  label?: string;    // provide for meaningful standalone icons; omit for decorative
  className?: string;
};

/**
 * Wraps a raw SVG path string into a standards-compliant SVG element.
 * Decorative icons (no label) get aria-hidden; labelled icons get role="img".
 */
export const makeIcon = (innerSvg: string, opts: IconOptions = {}): string => {
  const { size = 24, label, className } = opts;
  const a11y = label
    ? `role="img" aria-label="${label}"`
    : 'aria-hidden="true"';
  const classAttr = className ? ` class="${className}"` : '';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" ` +
    `width="${size}" height="${size}" fill="currentColor"${classAttr} ${a11y}>` +
    innerSvg +
    `</svg>`
  );
};

// ---------------------------------------------------------------------------
// Raw paths — stripped of Carbon's transparent-rect, defs, and title nodes.
// Exported so consumers can call makeIcon() with a custom size/label.
// ---------------------------------------------------------------------------

export const IconPaths = {

  // -- Theme -----------------------------------------------------------------
  /** Carbon: sunny */
  sun: [
    `<path d="M16,12a4,4,0,1,1-4,4,4.0045,4.0045,0,0,1,4-4m0-2a6,6,0,1,0,6,6,6,6,0,0,0-6-6Z" transform="translate(0 0.0049)"/>`,
    `<rect x="6.8536" y="5.3745" width="1.9998" height="4.958" transform="translate(-3.253 7.8584) rotate(-45)"/>`,
    `<rect x="2" y="15.0049" width="5" height="2"/>`,
    `<rect x="5.3745" y="23.1466" width="4.958" height="1.9998" transform="translate(-14.7739 12.6305) rotate(-45)"/>`,
    `<rect x="15" y="25.0049" width="2" height="5"/>`,
    `<rect x="23.1466" y="21.6675" width="1.9998" height="4.958" transform="translate(-10.0018 24.1514) rotate(-45)"/>`,
    `<rect x="25" y="15.0049" width="5" height="2"/>`,
    `<rect x="21.6675" y="6.8536" width="4.958" height="1.9998" transform="translate(1.5191 19.3793) rotate(-45)"/>`,
    `<rect x="15" y="2.0049" width="2" height="5"/>`,
  ].join(''),

  /** Carbon: asleep (crescent moon) */
  moon: `<path d="M13.5025,5.4136A15.0755,15.0755,0,0,0,25.096,23.6082a11.1134,11.1134,0,0,1-7.9749,3.3893c-.1385,0-.2782.0051-.4178,0A11.0944,11.0944,0,0,1,13.5025,5.4136M14.98,3a1.0024,1.0024,0,0,0-.1746.0156A13.0959,13.0959,0,0,0,16.63,28.9973c.1641.006.3282,0,.4909,0a13.0724,13.0724,0,0,0,10.702-5.5556,1.0094,1.0094,0,0,0-.7833-1.5644A13.08,13.08,0,0,1,15.8892,4.38,1.0149,1.0149,0,0,0,14.98,3Z"/>`,

  // -- UI Controls -----------------------------------------------------------
  /** Carbon: close */
  close: `<polygon points="17.4141 16 24 9.4141 22.5859 8 16 14.5859 9.4143 8 8 9.4141 14.5859 16 8 22.5859 9.4143 24 16 17.4141 22.5859 24 24 22.5859 17.4141 16"/>`,

  /** Carbon: checkmark */
  checkmark: `<polygon points="13 24 4 15 5.414 13.586 13 21.171 26.586 7.586 28 9 13 24"/>`,

  /** Carbon: warning (circle with exclamation) */
  warning: [
    `<path d="M16,2A14,14,0,1,0,30,16,14,14,0,0,0,16,2Zm0,26A12,12,0,1,1,28,16,12,12,0,0,1,16,28Z"/>`,
    `<rect x="15" y="8" width="2" height="11"/>`,
    `<path d="M16,22a1.5,1.5,0,1,0,1.5,1.5A1.5,1.5,0,0,0,16,22Z"/>`,
  ].join(''),

  /** Carbon: arrow--right */
  arrowRight: `<polygon points="18 6 16.57 7.393 24.15 15 4 15 4 17 24.15 17 16.57 24.573 18 26 28 16 18 6"/>`,

  // -- App Actions -----------------------------------------------------------
  /** Carbon: renew (circular refresh arrows) */
  refresh: [
    `<path d="M12,10H6.78A11,11,0,0,1,27,16h2A13,13,0,0,0,6,7.68V4H4v8h8Z"/>`,
    `<path d="M20,22h5.22A11,11,0,0,1,5,16H3a13,13,0,0,0,23,8.32V28h2V20H20Z"/>`,
  ].join(''),

  /** Carbon: location (pin with inner dot) */
  location: [
    `<path d="M16,18a5,5,0,1,1,5-5A5.0057,5.0057,0,0,1,16,18Zm0-8a3,3,0,1,0,3,3A3.0033,3.0033,0,0,0,16,10Z"/>`,
    `<path d="M16,30,7.5645,20.0513c-.0479-.0571-.3482-.4515-.3482-.4515A10.8888,10.8888,0,0,1,5,13a11,11,0,0,1,22,0,10.8844,10.8844,0,0,1-2.2148,6.5973l-.0015.0025s-.3.3944-.3447.4474ZM8.8125,18.395c.001.0007.2334.3082.2866.3744L16,26.9079l6.91-8.15c.0439-.0552.2783-.3649.2788-.3657A8.901,8.901,0,0,0,25,13,9,9,0,1,0,7,13a8.9054,8.9054,0,0,0,1.8125,5.395Z"/>`,
  ].join(''),

  /** Carbon: map (pin above a framed rectangle) */
  navigate: [
    `<path d="M16,24l-6.09-8.6A8.14,8.14,0,0,1,16,2a8.08,8.08,0,0,1,8,8.13,8.2,8.2,0,0,1-1.8,5.13ZM16,4a6.07,6.07,0,0,0-6,6.13,6.19,6.19,0,0,0,1.49,4L16,20.52,20.63,14A6.24,6.24,0,0,0,22,10.13,6.07,6.07,0,0,0,16,4Z"/>`,
    `<circle cx="16" cy="9" r="2"/>`,
    `<path d="M28,12H26v2h2V28H4V14H6V12H4a2,2,0,0,0-2,2V28a2,2,0,0,0,2,2H28a2,2,0,0,0,2-2V14A2,2,0,0,0,28,12Z"/>`,
  ].join(''),

  /** Carbon: pedestrian (walking figure) */
  walk: [
    `<path d="M21.6772,14l-1.2456-3.1143A2.9861,2.9861,0,0,0,17.646,9H13.5542a3.0018,3.0018,0,0,0-1.5439.4277L7,12.4336V18H9V13.5664l3-1.8V23.6973L8.5383,28.8906,10.2024,30,14,24.3027V11h3.646a.9949.9949,0,0,1,.9282.6289L20.3228,16H26V14Z"/>`,
    `<polygon points="17.051 18.316 19 24.162 19 30 21 30 21 23.838 18.949 17.684 17.051 18.316"/>`,
    `<path d="M16.5,8A3.5,3.5,0,1,1,20,4.5,3.5042,3.5042,0,0,1,16.5,8Zm0-5A1.5,1.5,0,1,0,18,4.5,1.5017,1.5017,0,0,0,16.5,3Z"/>`,
  ].join(''),

  // -- Park Types ------------------------------------------------------------
  /** Carbon: tree (deciduous with exposed roots) */
  tree: [
    `<path d="M12,30H9V28h3V15.5664L8.4854,13.4575l1.0292-1.7148,3.5147,2.1084A2.0115,2.0115,0,0,1,14,15.5664V28A2.0023,2.0023,0,0,1,12,30Z"/>`,
    `<path d="M22,30H19a2.0024,2.0024,0,0,1-2-2V17h6a4.0008,4.0008,0,0,0,3.981-4.396A4.1489,4.1489,0,0,0,22.7853,9H21.2016L21.025,8.221C20.452,5.6961,18.0308,4,15,4A6.02,6.02,0,0,0,9.5585,7.4859L9.25,8.1531l-.863-.1143A2.771,2.771,0,0,0,8,8a4,4,0,1,0,0,8v2A6,6,0,1,1,8,6c.0264,0,.0525,0,.0786.001A8.0271,8.0271,0,0,1,15,2c3.6788,0,6.6923,1.9776,7.7516,5h.0337a6.1641,6.1641,0,0,1,6.1872,5.4141A6.0011,6.0011,0,0,1,23,19l-4,0v9h3Z"/>`,
  ].join(''),

  /** Carbon: sprout (seedling breaking through soil) */
  sprout: `<path d="M25,2A11.01,11.01,0,0,0,14.7549,9.0244,6.9939,6.9939,0,0,0,9,6H6V9a7.0078,7.0078,0,0,0,7,7h1v9.0493a9.9229,9.9229,0,0,0-6.071,2.8794l1.414,1.4141a8,8,0,0,1,12.3086,1.2134l1.6616-1.1128A9.98,9.98,0,0,0,16,25.062V16h1A11.0125,11.0125,0,0,0,28,5V2ZM13,14A5.0057,5.0057,0,0,1,8,9V8H9a5.0054,5.0054,0,0,1,5,5v1ZM26,5a9.01,9.01,0,0,1-9,9H16V13a9.01,9.01,0,0,1,9-9h1Z"/>`,

  /** Carbon: bee (pollinates gardens) */
  bee: [
    `<path d="M16,10a6,6,0,0,0-6,6v8a6,6,0,0,0,12,0V16A6,6,0,0,0,16,10Zm-4.25,7.87h8.5v4.25h-8.5ZM16,28.25A4.27,4.27,0,0,1,11.75,24v-.13h8.5V24A4.27,4.27,0,0,1,16,28.25Zm4.25-12.13h-8.5V16a4.25,4.25,0,0,1,8.5,0Z"/>`,
    `<path d="M30.66,19.21,24,13v9.1a4,4,0,0,0,8,0A3.83,3.83,0,0,0,30.66,19.21ZM28,24.35a2.25,2.25,0,0,1-2.25-2.25V17l3.72,3.47h0A2.05,2.05,0,0,1,30.2,22,2.25,2.25,0,0,1,28,24.35Z"/>`,
    `<path d="M0,22.1a4,4,0,0,0,8,0V13L1.34,19.21A3.88,3.88,0,0,0,0,22.1Zm2.48-1.56h0L6.25,17v5.1a2.25,2.25,0,0,1-4.5,0A2.05,2.05,0,0,1,2.48,20.54Z"/>`,
    `<path d="M15,5.5A3.5,3.5,0,1,0,11.5,9,3.5,3.5,0,0,0,15,5.5Zm-5.25,0A1.75,1.75,0,1,1,11.5,7.25,1.77,1.77,0,0,1,9.75,5.5Z"/>`,
    `<path d="M20.5,2A3.5,3.5,0,1,0,24,5.5,3.5,3.5,0,0,0,20.5,2Zm0,5.25A1.75,1.75,0,1,1,22.25,5.5,1.77,1.77,0,0,1,20.5,7.25Z"/>`,
  ].join(''),

  /** Carbon: campsite (tent silhouette — fallback / unknown park type) */
  campsite: `<path d="M27.5618,26,17.17,8.9277,19.5361,5.04,17.8281,4,16,7.0049,14.17,4l-1.708,1.04,2.3665,3.8877L4.438,26H2v2H30V26ZM16,10.8506,25.2207,26H17V18H15v8H6.7791Z"/>`,

} as const;

// ---------------------------------------------------------------------------
// Pre-rendered icon strings at 24px, aria-hidden (decorative context).
// Import and drop directly into template literals: `${Icons.theme.sun}`
// ---------------------------------------------------------------------------

export const Icons = {

  /** Theme toggle buttons */
  theme: {
    sun:  makeIcon(IconPaths.sun),
    moon: makeIcon(IconPaths.moon),
  },

  /** Generic interface controls */
  ui: {
    close:      makeIcon(IconPaths.close),
    checkmark:  makeIcon(IconPaths.checkmark),
    warning:    makeIcon(IconPaths.warning),
    arrowRight: makeIcon(IconPaths.arrowRight),
  },

  /** Primary app actions */
  action: {
    refresh:  makeIcon(IconPaths.refresh),
    location: makeIcon(IconPaths.location),
    navigate: makeIcon(IconPaths.navigate),
    walk:     makeIcon(IconPaths.walk),
  },

  /** Park-type badges rendered inside park cards */
  parkType: {
    park:          makeIcon(IconPaths.tree),
    natureReserve: makeIcon(IconPaths.sprout),
    garden:        makeIcon(IconPaths.bee),
    fallback:      makeIcon(IconPaths.campsite),
  },

} as const;

// ---------------------------------------------------------------------------
// Park-type helper — drop-in replacement for the emoji getParkIcon() in ui.ts
// ---------------------------------------------------------------------------

export const getParkTypeIcon = (type: import('@/types').ParkType): string => {
  switch (type) {
    case 'park':           return Icons.parkType.park;
    case 'nature_reserve': return Icons.parkType.natureReserve;
    case 'garden':         return Icons.parkType.garden;
    default:               return Icons.parkType.fallback;
  }
};
